<template>
  <div class="profile">
    <div class="row">
      <div class="col-md-12">
        <div class="mx-4 my-4">
          <!-- Profile Avatar Section -->
          <div class="profile__avatar-card d-flex align-items-center p-3 mb-4">
            <div
              class="profile__avatar-wrapper"
              title="Click to change photo"
              @click="triggerFileInput"
            >
              <img
                :src="avatarDisplayUrl"
                class="profile__avatar-img"
                alt="Profile Avatar"
                @error="imageError = true"
              />
              <div class="profile__avatar-overlay">
                <i class="bx bx-camera"></i>
                <span>Change</span>
              </div>
            </div>

            <div class="profile__avatar-details ms-4">
              <h5 class="profile__avatar-name mb-1">
                {{ displayName || 'Profile Photo' }}
              </h5>
              <p class="profile__avatar-hint text-muted mb-2">
                Click your photo or the button below to upload and crop a new picture.
              </p>
              <div class="d-flex align-items-center gap-2">
                <button
                  type="button"
                  class="ed-btn__blim ed-btn__sm d-inline-flex align-items-center"
                  @click="triggerFileInput"
                >
                  <i class="bx bx-camera me-1"></i> Change Photo
                </button>
                <button
                  v-if="hasCustomAvatar"
                  type="button"
                  class="btn-remove-avatar d-inline-flex align-items-center"
                  @click="removeAvatar"
                >
                  <i class="bx bx-trash me-1"></i> Remove
                </button>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept="image/png, image/jpeg, image/jpg, image/webp"
                class="d-none"
                @change="onFileSelected"
              />
            </div>
          </div>

          <!-- Edit Profile Form -->
          <form @submit.prevent="updateProfile">
            <div class="row gx-5">
              <div class="col">
                <label for="firstName" class="form-label">First Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="firstName"
                  id="firstName"
                  aria-describedby="textHelp"
                />
                <div id="textHelp" class="form-text">
                  We'll never share your personal details with anyone else.
                </div>
              </div>
              <div class="col mb-3">
                <label for="lastName" class="form-label">Last Name</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="lastName"
                  id="lastName"
                  aria-describedby="textHelp"
                />
                <div id="textHelp" class="form-text">
                  We'll never share your personal details with anyone else.
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <label for="exampleInputEmail1" class="form-label"
                  >Email address</label
                >
                <input
                  type="email"
                  class="form-control"
                  v-model="email"
                  id="exampleInputEmail1"
                  aria-describedby="emailHelp"
                />
                <div id="emailHelp" class="form-text">
                  We'll never share your email with anyone else.
                </div>
              </div>
              <div class="col mb-3">
                <label for="exampleInputPassword1" class="form-label"
                  >Phone</label
                >
                <input
                  type="text"
                  class="form-control"
                  v-model="phone"
                  id="exampleInputPassword1"
                />
              </div>
            </div>

            <div class="mt-3">
              <div class="col mb-3">
                <label for="exampleInputPassword1" class="form-label"
                  >Eddie Key
                  <a target="_blank" href="https://beta.openai.com/login"
                    >(Open AI Key)</a
                  ></label
                >
                <input
                  type="text"
                  class="form-control"
                  v-model="apiKey"
                  id="exampleInputPassword1"
                />
              </div>
            </div>

            <div class="mb-3">
              <label for="exampleInputPassword1" class="form-label"
                >Address</label
              >
              <input
                type="text"
                class="form-control"
                v-model="address"
                id="exampleInputPassword1"
              />
            </div>

            <button type="submit" class="ed-btn" :disabled="isSubmitLoading">
              <span v-if="!isSubmitLoading">Submit</span>
              <span
                v-else
                class="spinner-border spinner-border-sm ml-2"
                :style="{ opacity: isSubmitLoading ? 1 : 0 }"
              ></span>
            </button>
          </form>
        </div>
      </div>
    </div>

    <!-- LinkedIn-Style Profile Photo Crop Modal -->
    <div
      v-if="isCropModalOpen"
      class="crop-modal"
      role="dialog"
      aria-modal="true"
    >
      <div class="crop-modal__backdrop" @click="closeCropModal"></div>
      <div class="crop-modal__dialog">
        <div class="crop-modal__content">
          <div class="crop-modal__header">
            <h5 class="crop-modal__title">Adjust your profile photo</h5>
            <button
              type="button"
              class="crop-modal__close-btn"
              aria-label="Close"
              @click="closeCropModal"
            >
              <i class="bx bx-x"></i>
            </button>
          </div>

          <div class="crop-modal__body">
            <div class="crop-modal__stage">
              <img
                ref="modalCropImage"
                :src="modalImageUrl"
                alt="Source photo"
                class="crop-modal__source-img"
              />
            </div>

            <!-- Toolbar (Zoom Slider + Rotate + Reset) -->
            <div class="crop-modal__toolbar">
              <div class="crop-modal__zoom-control">
                <button
                  type="button"
                  class="crop-tool-btn"
                  title="Zoom Out"
                  @click="stepZoom(-0.1)"
                >
                  <i class="bx bx-zoom-out"></i>
                </button>
                <input
                  type="range"
                  class="crop-zoom-slider"
                  :min="minZoom"
                  :max="maxZoom"
                  step="0.01"
                  :value="currentZoom"
                  @input="onZoomSliderChange($event.target.value)"
                />
                <button
                  type="button"
                  class="crop-tool-btn"
                  title="Zoom In"
                  @click="stepZoom(0.1)"
                >
                  <i class="bx bx-zoom-in"></i>
                </button>
              </div>

              <div class="crop-modal__actions">
                <button
                  type="button"
                  class="crop-tool-btn"
                  title="Rotate Left"
                  @click="rotateImage(-90)"
                >
                  <i class="bx bx-rotate-left"></i>
                  <span>Rotate</span>
                </button>
                <button
                  type="button"
                  class="crop-tool-btn"
                  title="Reset"
                  @click="resetCropper"
                >
                  <i class="bx bx-reset"></i>
                  <span>Reset</span>
                </button>
              </div>
            </div>
          </div>

          <div class="crop-modal__footer">
            <button
              type="button"
              class="ed-btn__blim ed-btn__sm"
              @click="closeCropModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="ed-btn ed-btn__sm"
              :disabled="isCropping"
              @click="applyCroppedImage"
            >
              <span v-if="!isCropping">Apply Photo</span>
              <span v-else class="spinner-border spinner-border-sm"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/9/99/Sample_User_Icon.png';

export default {
  data() {
    return {
      firstName: '',
      lastName: '',
      phone: '',
      address: '',
      email: '',
      apiKey: '',
      imageError: false,
      isSubmitLoading: false,

      // Modal Cropper state
      isCropModalOpen: false,
      modalImageUrl: '',
      cropper: null,
      currentZoom: 1,
      minZoom: 0.1,
      maxZoom: 3,
      isCropping: false,

      // Selected cropped image blob ready for submission
      pendingCroppedFile: null,
      localAvatarPreview: '',
    };
  },

  computed: {
    ...mapGetters({
      user: 'auth/authUser',
    }),

    displayName() {
      const parts = [this.firstName, this.lastName]
        .map((p) => (typeof p === 'string' ? p.trim() : ''))
        .filter((p) => p && p !== 'NA');
      return parts.length ? parts.join(' ') : (this.user?.username || '');
    },

    avatarDisplayUrl() {
      if (this.localAvatarPreview) {
        return this.localAvatarPreview;
      }
      const loc = this.user?.profileImage?.Location;
      if (loc && loc !== 'NA' && !this.imageError) {
        return loc;
      }
      return DEFAULT_AVATAR;
    },

    hasCustomAvatar() {
      if (this.localAvatarPreview) return true;
      const loc = this.user?.profileImage?.Location;
      return Boolean(loc && loc !== 'NA' && !this.imageError);
    },
  },

  mounted() {
    this.populateUserData(this.user);
  },

  beforeDestroy() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }
    if (this.modalImageUrl && this.modalImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.modalImageUrl);
    }
    if (this.localAvatarPreview && this.localAvatarPreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.localAvatarPreview);
    }
  },

  watch: {
    user(nextUser) {
      this.populateUserData(nextUser);
    },
  },

  methods: {
    ...mapActions('auth', ['resetPasswordstatus']),

    cleanVal(val) {
      return val && val !== 'NA' ? val : '';
    },

    populateUserData(u) {
      if (!u) return;
      this.firstName = this.cleanVal(u.firstName);
      this.lastName = this.cleanVal(u.lastName);
      this.phone = this.cleanVal(u.phone);
      this.email = this.cleanVal(u.email);
      this.address = this.cleanVal(u.address);
      this.apiKey = this.cleanVal(u.apiKey);
      this.imageError = false;
    },

    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;

      if (this.modalImageUrl && this.modalImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.modalImageUrl);
      }
      this.modalImageUrl = URL.createObjectURL(file);
      this.isCropModalOpen = true;

      this.$nextTick(() => {
        this.initCropper();
      });
    },

    initCropper() {
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      const image = this.$refs.modalCropImage;
      if (!image) return;

      this.cropper = new Cropper(image, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 0.85,
        restore: false,
        guides: false,
        center: false,
        highlight: false,
        cropBoxMovable: false,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        zoomOnWheel: true,
        background: false,
        ready: () => {
          const imageData = this.cropper.getImageData();
          const baseRatio = imageData.width / imageData.naturalWidth;
          this.currentZoom = baseRatio;
          this.minZoom = Math.max(0.1, baseRatio * 0.5);
          this.maxZoom = Math.max(2, baseRatio * 3.5);
        },
        zoom: (e) => {
          this.currentZoom = e.detail.ratio;
        },
      });
    },

    stepZoom(step) {
      if (!this.cropper) return;
      this.cropper.zoom(step);
    },

    onZoomSliderChange(val) {
      if (!this.cropper) return;
      const targetZoom = parseFloat(val);
      if (!Number.isNaN(targetZoom)) {
        this.cropper.zoomTo(targetZoom);
        this.currentZoom = targetZoom;
      }
    },

    rotateImage(degree) {
      if (!this.cropper) return;
      this.cropper.rotate(degree);
    },

    resetCropper() {
      if (!this.cropper) return;
      this.cropper.reset();
      const imageData = this.cropper.getImageData();
      this.currentZoom = imageData.width / imageData.naturalWidth;
    },

    applyCroppedImage() {
      if (!this.cropper) return;
      this.isCropping = true;

      try {
        const canvas = this.cropper.getCroppedCanvas({
          width: 320,
          height: 320,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
        });

        if (!canvas) {
          this.closeCropModal();
          return;
        }

        canvas.toBlob((blob) => {
          if (blob) {
            this.pendingCroppedFile = new File([blob], 'profile.jpg', {
              type: 'image/jpeg',
            });
            if (this.localAvatarPreview && this.localAvatarPreview.startsWith('blob:')) {
              URL.revokeObjectURL(this.localAvatarPreview);
            }
            this.localAvatarPreview = URL.createObjectURL(blob);
            this.imageError = false;
            if (this.$toast?.info) {
              this.$toast.info('Photo adjusted! Click Submit to save your profile.');
            }
          }
          this.closeCropModal();
        }, 'image/jpeg', 0.95);
      } catch (err) {
        console.error('Failed to crop image', err);
        this.closeCropModal();
      } finally {
        this.isCropping = false;
      }
    },

    closeCropModal() {
      this.isCropModalOpen = false;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
      if (this.modalImageUrl && this.modalImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.modalImageUrl);
        this.modalImageUrl = '';
      }
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    removeAvatar() {
      this.pendingCroppedFile = null;
      if (this.localAvatarPreview && this.localAvatarPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.localAvatarPreview);
      }
      this.localAvatarPreview = '';
      this.imageError = true;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async updateProfile() {
      this.isSubmitLoading = true;
      const profilePayload = {
        firstName: this.firstName || '',
        lastName: this.lastName || '',
        phone: this.phone || '',
        email: this.email || '',
        address: this.address || '',
        apiKey: this.apiKey || '',
      };

      const fd = new FormData();
      if (this.pendingCroppedFile) {
        fd.append('profile_upload', this.pendingCroppedFile);
      }

      Object.keys(profilePayload).forEach((key) => {
        fd.append(key, profilePayload[key]);
      });

      try {
        await this.$store.dispatch('auth/updateProfile', fd);
        this.pendingCroppedFile = null;
        this.imageError = false;
        this.$store.dispatch('eventToEventStore', {
          verb: 'PROFILE_UPDATE',
          value: { data: 'profile updated' },
        });
        if (this.$toast?.success) {
          this.$toast.success('Profile updated successfully');
        }
      } catch (error) {
        console.error('profile update failed', error);
        if (this.$toast?.error) {
          this.$toast.error('Failed to update profile');
        }
      } finally {
        this.isSubmitLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/abstracts/_variables.scss";

.profile {
  background: #fff;
  box-shadow: 0px 0px 1px #b8b8b8;
  border-radius: 0.3rem;

  &__avatar-card {
    background: #ffffff;
    border-radius: 0.3rem;
    border: 1px solid $color-grey-2;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  &__avatar-wrapper {
    position: relative;
    width: 104px;
    height: 104px;
    min-width: 104px;
    border-radius: 50%;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba($edvantaBlue, 0.12);
    border: 3px solid #fff;
    outline: 1px solid $color-grey-2;
    background: #fff;

    &:hover .profile__avatar-overlay {
      opacity: 1;
    }
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__avatar-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($edvantaBlue, 0.7);
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    font-size: 0.75rem;

    i {
      font-size: 1.4rem;
      margin-bottom: 2px;
    }
  }

  &__avatar-name {
    font-size: 1.15rem;
    font-weight: 600;
    color: $edvantaBlue;
  }

  &__avatar-hint {
    font-size: 0.85rem;
    color: $gray;
  }
}

.btn-remove-avatar {
  background: transparent;
  border: 1px solid $color-grey-1;
  color: $gray;
  padding: 0.25rem 0.85rem;
  border-radius: 0.2rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 1rem;
  }

  &:hover {
    border-color: $accentRed;
    color: $accentRed;
    background: rgba($accentRed, 0.05);
  }
}

/* ThoughtJumper Themed Profile Photo Crop Modal */
.crop-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--fontFamily, 'Poppins', sans-serif);

  &__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($edvantaBlue, 0.45);
    backdrop-filter: blur(4px);
  }

  &__dialog {
    position: relative;
    z-index: 10000;
    width: 100%;
    max-width: 480px;
    margin: 1.25rem;
    border-radius: 0.4rem;
    overflow: hidden;
    background: #ffffff;
    color: $black;
    box-shadow: 0 12px 36px rgba($edvantaBlue, 0.22);
    border: 1px solid $color-grey-2;
  }

  &__content {
    display: flex;
    flex-direction: column;
    background: #ffffff;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.9rem 1.25rem;
    background: #ffffff;
    border-bottom: 1px solid $color-grey-2;
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    color: $edvantaBlue;
  }

  &__close-btn {
    background: transparent;
    border: none;
    color: $gray;
    font-size: 1.5rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease, transform 0.15s ease;

    &:hover {
      color: $edvantaBlue;
      transform: scale(1.1);
    }
  }

  &__body {
    padding: 0;
    background: #f7f7f9;
  }

  &__stage {
    width: 100%;
    height: 350px;
    max-height: 50vh;
    background: #0a0e1a;
    overflow: hidden;
    position: relative;
  }

  &__source-img {
    display: block;
    max-width: 100%;
  }

  &__toolbar {
    padding: 1rem 1.25rem;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    border-top: 1px solid $color-grey-2;
  }

  &__zoom-control {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.85rem;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.25rem;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 0.85rem 1.25rem;
    background: $lightGray;
    border-top: 1px solid $color-grey-2;
  }
}

.crop-zoom-slider {
  flex: 1;
  max-width: 260px;
  height: 5px;
  cursor: pointer;
  accent-color: $edvantaBlue;
  border-radius: 3px;
}

.crop-tool-btn {
  background: #ffffff;
  border: 1px solid $color-grey-2;
  color: $edvantaBlue;
  border-radius: 0.2rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  transition: all 0.15s ease;

  i {
    font-size: 1.15rem;
  }

  &:hover {
    background: rgba($edvantaBlue, 0.05);
    border-color: $edvantaBlue;
    color: $edvantaBlue;
  }
}

/* Circular Stencil in ThoughtJumper Colors */
::v-deep .cropper-view-box {
  border-radius: 50%;
  outline: 2px solid $edvantaYellow;
  box-shadow: 0 0 0 1000px rgba(1, 1, 72, 0.65);
}

::v-deep .cropper-face {
  border-radius: 50%;
  background-color: transparent;
}

::v-deep .cropper-modal {
  opacity: 0.6;
  background-color: $edvantaBlue;
}
</style>
